import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Spinner, Tab, Table, Tabs } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    getRompslompCache, getRompslompSending, getRompslompStatus, saveRompslompToken, selectRompslompCompany, syncRompslomp,
    testRompslompConnection,
} from '../../../services/RompslompService';
import { isSuperAdmin } from '../../../services/AuthService';
import RompslompMappings from './RompslompMappings';
import RompslompPreview from './RompslompPreview';
import RompslompExpenses from './RompslompExpenses';
import RompslompInvoices from './RompslompInvoices';
import RompslompAuto from './RompslompAuto';
import RompslompAutoExpenses from './RompslompAutoExpenses';
import RompslompInvoiceDetail from './RompslompInvoiceDetail';
import RompslompSuppliers from './RompslompSuppliers';
import RompslompProducts from './RompslompProducts';
import RompslompLog from './RompslompLog';
import SettingsPage from '../Common/SettingsPage';
import { SendingBanner, SendingControls } from './RompslompSending';
import { apiMessage } from './rompslompUtils';

export { apiMessage };

const tokenSchema = (t) => Yup.object({
    token: Yup.string().trim().min(16, t('rompslomp.token.invalid')).max(512, t('rompslomp.token.invalid')).required(t('rompslomp.token.required')),
});

function Connection({ status, reload, sending, reloadSending }) {
    const { t } = useTranslation();
    const [testing, setTesting] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [companies, setCompanies] = useState(null);
    const [cache, setCache] = useState(null);

    const loadCache = useCallback(async () => {
        if (!status.company) { setCache(null); return; }
        try { setCache((await getRompslompCache()).data); } catch { setCache(null); }
    }, [status.company]);
    useEffect(() => { loadCache(); }, [loadCache]);

    const saveToken = async (values, { resetForm }) => {
        try {
            await saveRompslompToken(values.token.trim());
            resetForm();               // the token is never shown again
            setCompanies(null);
            toast.success(t('rompslomp.token.saved'));
            reload();
        } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); }
    };

    const test = async () => {
        setTesting(true);
        try { setCompanies((await testRompslompConnection()).data); } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); } finally { setTesting(false); }
    };

    const choose = async (id) => {
        try { await selectRompslompCompany(id); toast.success(t('rompslomp.company.selected')); reload(); } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); }
    };

    const sync = async () => {
        setSyncing(true);
        try { await syncRompslomp(); toast.success(t('rompslomp.sync.done')); await loadCache(); } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); } finally { setSyncing(false); }
    };

    return (
        <>
            <Card className="mb-3">
                <Card.Header><h5 className="mb-0">{t('rompslomp.sending.title')}</h5></Card.Header>
                <Card.Body><SendingControls sending={sending} reload={() => { reloadSending(); reload(); }} /></Card.Body>
            </Card>
            <Card className="mb-3">
                <Card.Header><h5 className="mb-0">{t('rompslomp.auto.title')}</h5></Card.Header>
                <Card.Body><RompslompAuto reloadSending={() => { reloadSending(); reload(); }} /></Card.Body>
            </Card>
            <Card className="mb-3">
                <Card.Header><h5 className="mb-0">{t('rompslomp.autoExpenses.title')}</h5></Card.Header>
                <Card.Body><RompslompAutoExpenses reloadSending={() => { reloadSending(); reload(); }} /></Card.Body>
            </Card>
            <Card className="mb-3">
                <Card.Header><h5 className="mb-0">{t('rompslomp.detail.title')}</h5></Card.Header>
                <Card.Body><RompslompInvoiceDetail sending={sending} reload={() => { reloadSending(); reload(); }} /></Card.Body>
            </Card>
            <Card className="mb-3">
                <Card.Header><h5 className="mb-0">{t('rompslomp.token.title')}</h5></Card.Header>
                <Card.Body>
                    <p>
                        {t('rompslomp.token.status')}:{' '}
                        <Badge bg={status.token_set ? 'success' : 'secondary'} data-testid="token-status">
                            {status.token_set ? t('rompslomp.token.set') : t('rompslomp.token.notSet')}
                        </Badge>
                    </p>
                    <Formik initialValues={{ token: '' }} validationSchema={tokenSchema(t)} onSubmit={saveToken}>
                        {({ handleSubmit, handleChange, handleBlur, values, errors, touched, isSubmitting }) => (
                            <Form noValidate onSubmit={handleSubmit} className="rompslomp-token-form">
                                <Form.Group className="mb-2" controlId="rompslomp-token">
                                    <Form.Label>{status.token_set ? t('rompslomp.token.replace') : t('rompslomp.token.label')}</Form.Label>
                                    <Form.Control
                                        type="password" name="token" autoComplete="off" value={values.token}
                                        onChange={handleChange} onBlur={handleBlur} isInvalid={touched.token && !!errors.token}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.token}</Form.Control.Feedback>
                                    <Form.Text className="text-muted">{t('rompslomp.token.hint')}</Form.Text>
                                </Form.Group>
                                <Button type="submit" size="sm" disabled={isSubmitting}>{t('rompslomp.token.save')}</Button>
                            </Form>
                        )}
                    </Formik>
                </Card.Body>
            </Card>

            <Card className="mb-3">
                <Card.Header><h5 className="mb-0">{t('rompslomp.company.title')}</h5></Card.Header>
                <Card.Body>
                    <Button size="sm" variant="outline-primary" onClick={test} disabled={!status.token_set || testing}>
                        {testing ? <Spinner size="sm" animation="border" /> : t('rompslomp.test.button')}
                    </Button>
                    {companies && (
                        <div className="mt-3" data-testid="company-list">
                            {companies.user && (companies.user.name || companies.user.email) && (
                                <p data-testid="token-owner">{t('rompslomp.test.owner')}: <strong>{companies.user.name}</strong> {companies.user.email && `<${companies.user.email}>`}</p>
                            )}
                            {companies.companies.length === 0 && <Alert variant="warning">{t('rompslomp.company.none')}</Alert>}
                            {companies.companies.length > 1 && <Alert variant="warning">{t('rompslomp.company.several')}</Alert>}
                            <Table size="sm" responsive>
                                <thead><tr><th>{t('rompslomp.company.name')}</th><th>{t('rompslomp.company.id')}</th><th>{t('rompslomp.company.api')}</th><th /></tr></thead>
                                <tbody>
                                    {companies.companies.map((c) => (
                                        <tr key={c.id}>
                                            <td>{c.name}</td>
                                            <td>{c.id}</td>
                                            <td>{c.api_accessible === false ? t('rompslomp.company.apiNo') : t('rompslomp.company.apiYes')}</td>
                                            <td className="text-end">
                                                {status.company?.id === c.id
                                                    ? <Badge bg="success">{t('rompslomp.company.current')}</Badge>
                                                    : <Button size="sm" disabled={c.api_accessible === false} onClick={() => choose(c.id)}>{t('rompslomp.company.select')}</Button>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Card>
                <Card.Header><h5 className="mb-0">{t('rompslomp.sync.title')}</h5></Card.Header>
                <Card.Body>
                    <p className="text-muted">{t('rompslomp.sync.hint')}</p>
                    <Button size="sm" variant="outline-primary" onClick={sync} disabled={!status.company || syncing}>
                        {syncing ? <Spinner size="sm" animation="border" /> : t('rompslomp.sync.button')}
                    </Button>
                    {cache && (
                        <Table size="sm" responsive className="mt-3">
                            <thead><tr><th>{t('rompslomp.sync.kind')}</th><th>{t('rompslomp.sync.count')}</th><th>{t('rompslomp.sync.state')}</th></tr></thead>
                            <tbody>
                                {['accounts', 'vat_types', 'contacts', 'sales_invoices'].map((k) => (
                                    <tr key={k}>
                                        <td>{t(`rompslomp.sync.kinds.${k}`)}</td>
                                        <td>{cache.cache[k]?.count ?? '-'}</td>
                                        <td>{cache.cache[k] ? (cache.cache[k].status === 'ok' ? cache.cache[k].synced_at : cache.cache[k].note) : t('rompslomp.sync.never')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>
        </>
    );
}

export default function RompslompSettings() {
    const { t } = useTranslation();
    const [status, setStatus] = useState(null);
    const [sending, setSending] = useState(null);
    const [error, setError] = useState(null);

    const loadSending = useCallback(async () => {
        try { setSending((await getRompslompSending()).data); } catch (err) { setError(apiMessage(err, t, 'rompslomp.errors.generic')); }
    }, [t]);
    const load = useCallback(async () => {
        try { setStatus((await getRompslompStatus()).data); setError(null); } catch (err) { setError(apiMessage(err, t, 'rompslomp.errors.generic')); }
        loadSending();
    }, [t, loadSending]);
    useEffect(() => { if (isSuperAdmin()) load(); }, [load]);

    const frame = (children) => <SettingsPage title={t('rompslomp.title')} className="rompslomp-settings">{children}</SettingsPage>;
    if (!isSuperAdmin()) return frame(<Alert variant="warning">{t('rompslomp.errors.superAdminOnly')}</Alert>);
    if (error) return frame(<Alert variant="danger">{error}</Alert>);
    if (!status || !sending) return frame(<Spinner animation="border" />);

    return (
        <SettingsPage title={t('rompslomp.title')} subtitle={t('rompslomp.phase2')} className="rompslomp-settings"
            banners={(
                <>
                    {/* shown on every Rompslomp screen, inside the padded page frame */}
                    <Alert variant={status.company ? 'info' : 'warning'} data-testid="company-banner">
                        {status.company
                            ? <>{t('rompslomp.banner.company')}: <strong>{status.company.name}</strong></>
                            : t('rompslomp.banner.none')}
                    </Alert>
                    <SendingBanner sending={sending} company={status.company} />
                </>
            )}>
            <Tabs defaultActiveKey="connection" className="mb-3" mountOnEnter unmountOnExit>
                <Tab eventKey="connection" title={t('rompslomp.tabs.connection')}><Connection status={status} reload={load} sending={sending} reloadSending={loadSending} /></Tab>
                <Tab eventKey="mappings" title={t('rompslomp.tabs.mappings')} disabled={!status.company}><RompslompMappings /></Tab>
                <Tab eventKey="suppliers" title={t('rompslomp.tabs.suppliers')} disabled={!status.company}><RompslompSuppliers sending={sending} reloadSending={loadSending} /></Tab>
                <Tab eventKey="products" title={t('rompslomp.tabs.products')} disabled={!status.company}><RompslompProducts sending={sending} reloadSending={loadSending} /></Tab>
                <Tab eventKey="preview" title={t('rompslomp.tabs.preview')} disabled={!status.company}><RompslompPreview sending={sending} reloadSending={loadSending} /></Tab>
                <Tab eventKey="expenses" title={t('rompslomp.tabs.expenses')} disabled={!status.company}><RompslompExpenses sending={sending} reloadSending={loadSending} /></Tab>
                <Tab eventKey="invoices" title={t('rompslomp.tabs.invoices')} disabled={!status.company}><RompslompInvoices sending={sending} reloadSending={loadSending} /></Tab>
                <Tab eventKey="log" title={`${t('rompslomp.tabs.log')}${sending.needs_attention ? ` (${sending.needs_attention})` : ''}`}><RompslompLog reloadSending={loadSending} /></Tab>
            </Tabs>
        </SettingsPage>
    );
}
